<template>
    <div class="chart-wrapper bar-chart-wrapper">
        <canvas :id="id"></canvas>
    </div>
</template>

<script>
import Chart from 'chart.js';
import { isInViewport } from 'src/util/helpers';

Chart.defaults.global.defaultFontFamily = 'Roboto';

const lineGraphColor = 'rgba(76, 175, 80, .6)';

export default {
    props: {
        tooltips: {
            type: Boolean,
            default: true,
        },
        displayY: {
            type: Boolean,
            default: true,
        },
        displayX: {
            type: Boolean,
            default: true,
        },
        displayYGrid: {
            type: Boolean,
            default: true,
        },
        displayXGrid: {
            type: Boolean,
            default: true,
        },
        displayLegend: {
            type: Boolean,
            default: true,
        },
        suggestedYMax: {
            type: Number,
            default: null,
        },
        borderColor: {
            type: String,
            default: 'rgba(76, 175, 80, 1)',
        },
        backgroundColor: {
            type: String,
            default: lineGraphColor,
        },
        chartData: {
            type: Array,
            default: () => [],
        },
        annotation: {
            type: Object,
            default: () => ({}),
        },
        animate: {
            type: Boolean,
            default: null,
        },
    },
    data() {
        return {
            id:
                'chart-' +
                Math.random()
                    .toString()
                    .replace('.', ''),
        };
    },
    methods: {
        renderChart() {
            if (this.chart) {
                this.chart.destroy();
            }

            const ctx = document.getElementById(this.id).getContext('2d');

            this.chart = new Chart(ctx, this.chartOptions);

            // console.log(this.chart.toBase64Image());
        },
        isInViewport() {
            return isInViewport(this.$el);
        },
    },
    watch: {
        chartData: function() {
            this.renderChart();
        },
    },
    computed: {
        chartOptions() {
            return {
                type: 'bar',
                data: {
                    labels: this.chartData.map(item => item.y),
                    datasets: this.datasets,
                },
                options: this.options,
            };
        },
        options() {
            return {
                animation: this.animation,
                maintainAspectRatio: false,
                elements: {
                    point: {
                        radius: 0,
                        backgroundColor: this.backgroundColor,
                        borderColor: '#fff',
                        borderWidth: 1,
                    },
                },
                legend: {
                    display: this.displayLegend,
                    reverse: true,
                    labels: {
                        fontColor: 'rgba(255, 255, 255, .5)',
                        boxWidth: 12,
                    },
                },
                tooltips: {
                    enabled: this.tooltips,
                    intersect: false,
                    xPadding: 12,
                    yPadding: 12,
                    mode: 'index',
                    displayColors: false,
                    backgroundColor: '#fff',
                    titleFontColor: '#272727',
                    bodyFontColor: '#272727',
                    callbacks: {
                        beforeLabel: (tooltipItem, data) => {
                            const config = this.chartData[tooltipItem.index].x[
                                tooltipItem.datasetIndex
                            ];

                            return config.label;
                        },
                        label: (tooltipItem, data) => {
                            const config = this.chartData[tooltipItem.index].x[
                                tooltipItem.datasetIndex
                            ];

                            return config.filter
                                ? Vue.filter(config.filter)(tooltipItem.yLabel)
                                : tooltipItem.yLabel;
                        },
                    },
                },
                scales: {
                    yAxes: [
                        {
                            display: this.displayY,
                            gridLines: {
                                display: this.displayYGrid,
                                color: '#4d4d4d',
                            },
                            ticks: {
                                beginAtZero: true,
                                callback(value, index, values) {
                                    return Vue.filter('money')(value);
                                },
                                fontColor: '#fff',
                                suggestedMax: this.suggestedYMax,
                            },
                        },
                    ],
                    xAxes: [
                        {
                            display: this.displayX,
                            gridLines: {
                                display: this.displayXGrid,
                                color: '#4d4d4d',
                            },
                            ticks: {
                                fontColor: '#fff',
                            },
                        },
                    ],
                },
                annotation: this.annotation,
                ...(this.tooltips
                    ? {}
                    : {
                          events: [],
                      }),
            };
        },
        datasets() {
            return this.chartData
                .map(item => item.x)
                .reduce((prev, cur) => {
                    cur.map((item, key) => {
                        if (prev.length === key) {
                            prev.push([]);
                        }

                        prev[key].push(item);
                    });
                    return prev;
                }, [])
                .map((set, index) => ({
                    lineTension: 0,
                    label: set[0].label,
                    data: set.map(item => item.value),
                    backgroundColor: this.gradientStrokes[index],
                    borderColor: this.borderColors[index],
                    borderWidth: 1,
                }));
        },
        animation() {
            if (this.animate === false) {
                return false;
            }

            const options = {
                easing: 'easeOutQuint',
            };

            if (this.animate === true) {
                return options;
            }

            if (this.isInViewport()) {
                return options;
            }

            return false;
        },
    },
    mounted() {
        const ctx = document.getElementById(this.id).getContext('2d');

        this.borderColors = ['rgba(76, 175, 80, 1)', 'rgba(255, 255, 255, .7)'];

        const gradients = [
            {
                start: 'rgba(76, 175, 80, .2)',
                end: 'rgba(76, 175, 80, 0)',
            },
            {
                start: 'rgba(255, 255, 255, .2)',
                end: 'rgba(255, 255, 255, 0)',
            },
        ];

        const chartHeight = this.$el.getBoundingClientRect().height;

        this.gradientStrokes = gradients.map(gradient => {
            const stroke = ctx.createLinearGradient(0, 0, 0, chartHeight);

            stroke.addColorStop(0, gradient.start);
            stroke.addColorStop(1, gradient.end);

            return stroke;
        });

        this.renderChart();
    },
};
</script>

